import { LeadMethod, type GenericLead } from "~/types/genericLead";
import type { Utms } from "~/types/utm";

export const createGenericLead = async (genericLeadData: GenericLead) => {
  const debug = getDebugValue();
  const cfData = await useCfRequest();
  const { href } = useRequestURL();
  const { $Fullstory } = useNuxtApp();
  const utms = useState("utms");
  const utmObj: Utms = utms.value || {};

  if (
    genericLeadData &&
    genericLeadData.email_address?.includes("@team.aussiebroadband.com.au")
  ) {
    console.log("[Lead Generation Bypassed]: Staff email address detected.");
    return;
  }

  const leadData = {
    ...utmObj,
    ...genericLeadData,
  };

  leadData.lead_url = href;

  let eventValue = 20; // always has name, email, phone
  if (genericLeadData) {
    eventValue = (genericLeadData.locid ?? false) ? 30 : eventValue;
    eventValue =
      genericLeadData.property_type === "business" &&
      !["gmail", "yahoo", "hotmail", "live", "outlook", "bigpond", "mac"].some(
        (email) => genericLeadData.email_address.includes(email),
      )
        ? 50
        : eventValue;
    eventValue =
      cfData?.asOrganization === "Aussie Broadband" ? 60 : eventValue;
  }

  // resolve enum value if an enum key was passed.
  if (
    leadData &&
    Object.keys(LeadMethod).includes(
      leadData.lead_source as keyof typeof LeadMethod,
    )
  ) {
    leadData.lead_source =
      LeadMethod[genericLeadData.lead_source as keyof typeof LeadMethod];
  }

  let targetURL = "";
  let request = {};

  // wrapper fails with a 502 if a contact_name field isn't passed on
  // if no contact_field passed in but we have first and last name
  if (!leadData.contact_name && (leadData.first_name || leadData.last_name)) {
    leadData.contact_name = leadData.first_name + " " + leadData.last_name;
  } else {
    // warning for future - hopefully help to avoid headaches with unhelpful wrapper errors
    if (debug) {
      console.log(
        "[useGenericLead] WARNING: No contact_name passed in, and no first or last name to create one",
      );
    }
  }

  if (
    (["enterprise"].includes(leadData.property_type) &&
      Number(leadData.number_of_employees) >= 100) ||
    ["wholesale"].includes(leadData.property_type)
  ) {
    //salesforce
    targetURL = `/api/sf/`;

    leadData.number_of_employees = isNaN(Number(leadData.number_of_employees))
      ? undefined
      : leadData.number_of_employees;

    leadData.interests =
      leadData.interests &&
      Array.isArray(leadData.interests) &&
      leadData.interests.length > 0
        ? leadData.interests.join(", ")
        : "none";
    leadData.ILANDPAGE = leadData.ILANDPAGE && leadData.ILANDPAGE.split("?")[0]; // remove url params for salesforce length limit.
    leadData.lead_url = leadData.lead_url.split("?")[0]; // remove url params for salesforce length limit.
    const searchParams = new URLSearchParams(
      Object.entries(leadData).reduce<Record<string, string>>(
        (acc, [key, value]) => {
          acc[key] =
            value !== undefined && value !== null ? String(value) : "undefined";
          return acc;
        },
        {},
      ),
    );

    request = {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        "Cache-Control": "no-cache",
      },
      body: searchParams,
    };
  } else {
    // widesales
    targetURL = getApiUrl("/genericlead/");

    // override business lead priorities
    if (["business", "business_account"].includes(leadData.property_type)) {
      leadData.lead_priority = leadData.lead_priority != 20 ? 10 : 4;
    }

    request = {
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Accept: "application/json",
      },
      body: JSON.stringify({ ...leadData }),
    };
  }

  await $fetch(targetURL, {
    method: "POST",
    ...request,
    onRequestError({ request, error }) {
      if (debug) console.log("[GenericLead request error]", request, error);
      throw createError({
        message:
          error.message ||
          "We are unable to send your request at the moment, please try again.",
      });
    },
    onResponse({ response }) {
      $Fullstory("setProperties", {
        type: "user",
        properties: {
          lead_ref: response._data?.lead_ref,
          loc_id: genericLeadData.locid,
        },
      });

      const gtm = useGtm();
      gtm?.trackEvent({
        event: "leadCreated",
        lead_source: genericLeadData.lead_source ?? "unknown",
        type: genericLeadData.property_type ?? "unknown",
        value: eventValue,
      });
      if (debug) console.log("[GenericLead response Success]", response._data);
      return response._data;
    },
    onResponseError({ request, response }) {
      if (debug) {
        console.log(
          "[GenericLead response error]",
          request,
          response.status,
          response._data,
        );
      }
      throw createError({
        statusCode: response.status,
        message:
          response._data.message ||
          "We are unable to process your request at the moment, please try again.",
      });
    },
  });
};
